import { useEffect, useRef, useState } from "react";
import AdminLogin from "@/pages/admin-login";
import AdminDashboard from "@/pages/admin-dashboard";
import AdminOrgDetail from "@/pages/admin-org-detail";
import AdminContractDetail from "@/pages/admin-contract-detail";
import AdminCoachDetail from "@/pages/admin-coach-detail";
import AdminHealth from "@/pages/admin-health";
import AdminDemoLeads from "@/pages/admin-demo-leads";
import { ImpersonationBanner } from "@/components/ImpersonationBanner";
import { getPlanBadgeStyle } from "@/lib/planBadge";
import { computeSidebarGroups } from "@/lib/sidebarOrdering";
import { AdminGuard } from "@/components/AdminGuard";
import { ClerkProvider, SignIn, SignUp, Show, useClerk, useUser, useSignIn, useAuth } from "@clerk/react";
import { Sentry } from "@/lib/sentry";
import { shadcn } from "@clerk/themes";
import { Switch, Route, useLocation, useSearch, Router as WouterRouter, Redirect, Link } from "wouter";
import { QueryClientProvider, useQueryClient } from "@tanstack/react-query";
import { queryClient } from "./lib/queryClient";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";

import { OrgProvider, useOrg } from "@/lib/orgContext";
import { TwoFactorGuard } from "@/components/TwoFactorGuard";
import { OnboardingProvider } from "@/lib/onboarding";
import { ProductTour } from "@/components/ProductTour";
import Landing from "@/pages/landing";
import Onboarding from "@/pages/onboarding";
import Dashboard from "@/pages/dashboard";
import Contracts from "@/pages/contracts";
import Timeline from "@/pages/timeline";
import ContractNew from "@/pages/contracts-new";
import ContractDetail from "@/pages/contracts-detail";
import TermSheetNew from "@/pages/term-sheets-new";
import TermSheetDetail from "@/pages/term-sheets-detail";
import ContractsImport from "@/pages/contracts-import";
import Coaches from "@/pages/coaches";
import CoachPortal from "@/pages/coach-portal";
import CoachNew from "@/pages/coaches-new";
import CoachDetail from "@/pages/coaches-detail";
import Counterparties from "@/pages/counterparties";
import Insurance from "@/pages/insurance";
import Mitigations from "@/pages/mitigations";
import Renewals from "@/pages/renewals";
import Settings from "@/pages/settings";
import Notifications from "@/pages/notifications";
import Approvals from "@/pages/approvals";
import Negotiations from "@/pages/negotiations";
import AuditLog from "@/pages/audit-log";
import ClauseLibrary from "@/pages/clause-library";
import ClauseEditor from "@/pages/clause-editor";
import ClauseDetail from "@/pages/clause-detail";
import UnlockRequests from "@/pages/unlock-requests";
import Templates from "@/pages/templates";
import TemplatesEditor from "@/pages/templates-editor";
import SignPage from "@/pages/sign";
import ApprovalActionPage from "@/pages/approval-action";
import Analytics from "@/pages/analytics";
import Equity from "@/pages/equity";
import Reports from "@/pages/reports";
import Finance from "@/pages/finance";
import CompensationVerification from "@/pages/compensation-verification";
import Terms from "@/pages/terms";
import Privacy from "@/pages/privacy";
import SecurityPage from "@/pages/security";
import Subprocessors from "@/pages/subprocessors";
import GuestAccept from "@/pages/guest-accept";
import CoachPortalAccept from "@/pages/coach-portal-accept";
import GuestContractPage from "@/pages/guest-contract";
import NotFound from "@/pages/not-found";

import {
  SidebarProvider,
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuItem,
  SidebarMenuButton,
  SidebarHeader,
  SidebarFooter,
  SidebarTrigger,
} from "@/components/ui/sidebar";
import { Button } from "@/components/ui/button";
import { LayoutDashboard, Users, FileText, ShieldCheck, AlertTriangle, LogOut, ChevronDown, ChevronRight, Settings as SettingsIcon, Inbox, RefreshCw, CalendarRange, Moon, Sun, Sparkles, Loader2, DollarSign, UserCog, Scale, History, BookOpen, Layers, BarChart3, ArrowLeftRight, KeyRound, ScrollText, FileBarChart } from "lucide-react";
import { useGetClauseUnlockRequestPendingCount, getGetClauseUnlockRequestPendingCountQueryKey } from "@workspace/api-client-react";
import { useTheme } from "@/hooks/use-theme";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { NotificationCenter } from "@/components/NotificationCenter";
import { MentionsInbox } from "@/components/MentionsInbox";
import { CommandPalette } from "@/components/CommandPalette";
import { CookieConsent } from "@/components/CookieConsent";
import { HubSpotTracker } from "@/components/HubSpotTracker";
import { LifecycleBanner } from "@/components/LifecycleBanner";
import { DemoBanner } from "@/components/DemoBanner";
import { DemoSessionTimer } from "@/components/DemoSessionTimer";
import { initAnalytics } from "@/lib/analytics";
import { salesInquiryHref } from "@/lib/support";
import { AppFooter } from "@/components/AppFooter";
import { AppErrorBoundary } from "@/components/AppErrorBoundary";
import { ReadOnlyToastHandler } from "@/components/ReadOnlyToastHandler";
import { useVersionDriftToast } from "@/lib/versionDrift";

function VersionDriftHandler() {
  useVersionDriftToast();
  return null;
}
import { PersonaBadge } from "@/components/PersonaBadge";
import type { Persona } from "@/lib/orgContext";
import { useActivePersona, ACTIVE_PERSONA_STORAGE_KEY } from "@/lib/activePersona";

// Use the build-time publishable key directly. Previously this was derived
// from window.location.hostname via publishableKeyFromHost — a multi-tenant
// pattern that, with a prod (pk_live) key, rebuilds the key from the current
// host and so mints tokens against a host-dependent FAPI domain. This single
// production instance lives on clerk.zindeal.com (CNAME mode), so the env key
// must be used as-is to keep the SPA and API verifying against the same FAPI.
const clerkPubKey = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY as string | undefined;
const clerkProxyUrl = import.meta.env.VITE_CLERK_PROXY_URL;
const basePath = import.meta.env.BASE_URL.replace(/\/$/, "");

// Derive the Clerk JS URL from the publishable key's embedded Frontend API
// host instead of hard-coding a single instance. The publishable key encodes
// its FAPI domain (base64, trailing "$"), e.g. pk_test_... -> modest-javelin-74
// .clerk.accounts.dev and pk_live_... -> clerk.zindeal.com. This makes the same
// build work for BOTH the dev and production Clerk instances with no edits:
// swap the VITE_CLERK_PUBLISHABLE_KEY build-arg and the SDK loads from the
// matching instance automatically.
function clerkFrontendApiFromKey(pk: string | undefined): string {
  if (!pk) return "";
  try {
    const encoded = pk.replace(/^pk_(test|live)_/, "");
    const decoded = atob(encoded);
    return decoded.replace(/\$+$/, "");
  } catch {
    return "";
  }
}

const clerkFapiHost = clerkFrontendApiFromKey(clerkPubKey);
const clerkJsUrl = clerkFapiHost
  ? `https://${clerkFapiHost}/npm/@clerk/clerk-js@6/dist/clerk.browser.js`
  : "https://modest-javelin-74.clerk.accounts.dev/npm/@clerk/clerk-js@6/dist/clerk.browser.js";

function stripBase(path: string): string {
  return basePath && path.startsWith(basePath)
    ? path.slice(basePath.length) || "/"
    : path;
}

if (!clerkPubKey) {
  throw new Error("Missing VITE_CLERK_PUBLISHABLE_KEY");
}

const clerkAppearance = {
  theme: shadcn,
  cssLayerName: "clerk",
  options: {
    logoPlacement: "inside" as const,
    logoLinkUrl: basePath || "/",
    logoImageUrl: `${window.location.origin}${basePath}/logo.svg`,
  },
  variables: {
    colorPrimary: "#0B1834",
    colorForeground: "#0f1923",
    colorMutedForeground: "#5a6a7e",
    colorDanger: "#ef4444",
    colorBackground: "#ffffff",
    colorInput: "#f4f6f9",
    colorInputForeground: "#0f1923",
    colorNeutral: "#dde3ec",
    fontFamily: "Inter, system-ui, sans-serif",
    borderRadius: "0.75rem",
  },
  elements: {
    rootBox: "w-full flex justify-center",
    cardBox: "bg-white rounded-2xl w-[440px] max-w-full overflow-hidden shadow-xl border border-gray-100",
    card: "!shadow-none !border-0 !bg-transparent !rounded-none",
    footer: "!shadow-none !border-0 !bg-transparent !rounded-none",
    headerTitle: "text-gray-900 font-bold text-2xl tracking-tight",
    headerSubtitle: "text-gray-500 text-sm",
    socialButtonsBlockButtonText: "text-gray-700 font-medium",
    formFieldLabel: "text-gray-700 font-medium text-sm",
    footerActionLink: "text-[#0B1834] font-semibold hover:text-blue-800",
    footerActionText: "text-gray-500",
    dividerText: "text-gray-400",
    identityPreviewEditButton: "text-[#0B1834]",
    formFieldSuccessText: "text-green-600",
    alertText: "text-gray-700",
    logoBox: "mt-2",
    logoImage: "h-10 w-auto",
    socialButtonsBlockButton: "border border-gray-200 bg-white hover:bg-gray-50",
    formButtonPrimary: "bg-[#0B1834] hover:bg-[#16284a] text-white font-semibold",
    formFieldInput: "border-gray-200 bg-gray-50 text-gray-900 focus:border-[#0B1834] focus:ring-1 focus:ring-[#0B1834]",
    footerAction: "bg-gray-50 border-t border-gray-100",
    dividerLine: "bg-gray-200",
    alert: "bg-red-50 border border-red-100",
    otpCodeFieldInput: "border-gray-200",
    formFieldRow: "gap-3",
    main: "gap-4",
  },
};

// Sidebar plan badge is rendered via the shared planBadge helper
// (variant: "sidebar") so labels and colors stay consistent with the
// admin console and never drift from PLAN_META. See artifacts/app/src/lib/planBadge.ts.

type NavItem = {
  href: string;
  label: string;
  icon: React.ElementType;
  tour?: string;
  personaHighlight?: Persona[];
  adminOnly?: boolean;
  financeOnly?: boolean;
  legalOnly?: boolean;
  showBadgeKind?: "unlockRequests";
};

const NAV_ITEMS: { label: string; items: NavItem[] }[] = [
  {
    label: "Overview",
    items: [
      { href: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
      { href: "/finance", label: "Finance", icon: DollarSign, personaHighlight: ["Finance"], financeOnly: true },
      { href: "/analytics", label: "Analytics", icon: BarChart3, personaHighlight: ["Finance"], financeOnly: true },
      { href: "/reports", label: "Reports", icon: FileBarChart, personaHighlight: ["Finance", "HR", "Legal", "SA"] },
    ],
  },
  {
    label: "Management",
    items: [
      { href: "/coaches", label: "Coaches", icon: Users, tour: "nav-coaches", personaHighlight: ["HR"] },
      { href: "/term-sheets", label: "Term Sheets", icon: ScrollText, personaHighlight: ["Legal", "HR"] },
      { href: "/contracts", label: "Contracts", icon: FileText, tour: "nav-contracts", personaHighlight: ["Finance"] },
      { href: "/negotiations", label: "Negotiations", icon: ArrowLeftRight, personaHighlight: ["Legal", "Finance"] },
      { href: "/approvals", label: "Approvals", icon: Inbox },
      { href: "/renewals", label: "Renewals", icon: RefreshCw, tour: "nav-renewals", personaHighlight: ["Finance", "HR"] },
      { href: "/timeline", label: "Timeline", icon: CalendarRange },
    ],
  },
  {
    label: "Compliance",
    items: [
      { href: "/insurance", label: "Insurance", icon: ShieldCheck, tour: "nav-insurance", personaHighlight: ["HR"] },
      { href: "/mitigations", label: "Risk Tracker", icon: AlertTriangle, personaHighlight: ["Legal"] },
      { href: "/equity", label: "Equity", icon: Scale, personaHighlight: ["Legal"] },
      { href: "/audit-log", label: "Audit Log", icon: History, personaHighlight: ["Legal"] },
    ],
  },
  // Reference / library surfaces. Demoted from the main nav so they don't
  // compete with daily workflow items. Still reachable here for admins and
  // surfaced contextually from inside contracts, templates and the negotiation
  // flow.
  {
    label: "Library",
    items: [
      { href: "/templates", label: "Templates", icon: Layers, personaHighlight: ["HR", "Legal"] },
      { href: "/clause-library", label: "Clause Library", icon: BookOpen, tour: "nav-clauses", personaHighlight: ["Legal"] },
      { href: "/counterparties", label: "Counterparties", icon: Users },
      { href: "/unlock-requests", label: "Unlock Requests", icon: KeyRound, personaHighlight: ["Legal"], legalOnly: true, showBadgeKind: "unlockRequests" },
    ],
  },
];

// Persona-dedicated workspace groups. Each persona gets a labelled sidebar
// section listing the surfaces most relevant to their daily workflow. Items
// are looked up by href from NAV_ITEMS so the icons / tours / badges stay in
// one place. When a persona group is shown, those items are stripped from
// their original groups so they don't appear twice. The group is shown when
// the user's effectivePersona matches OR they're an admin (admins see every
// persona workspace, mirroring the original Finance pattern).
// Task #535: each persona's dedicated group now spans the full natural
// workflow for that role so the sidebar feels like a complete workspace —
// not a rump list — once a persona is active. Items listed here are
// stripped from their original NAV_ITEMS group so they appear exactly
// once. Order matches the persona's typical click path.
const PERSONA_GROUP_DEFS: { persona: Persona; label: string; hrefs: string[] }[] = [
  {
    persona: "Finance",
    label: "Finance",
    hrefs: ["/finance", "/analytics", "/contracts", "/renewals", "/equity", "/reports"],
  },
  {
    persona: "HR",
    label: "HR",
    hrefs: ["/coaches", "/term-sheets", "/contracts", "/renewals", "/insurance", "/reports"],
  },
  {
    persona: "Legal",
    label: "Legal",
    hrefs: [
      "/templates",
      "/clause-library",
      "/term-sheets",
      "/contracts",
      "/negotiations",
      "/approvals",
      "/mitigations",
      "/equity",
      "/unlock-requests",
      "/audit-log",
    ],
  },
  // SA = Sport Administrator — supervises coaches across sports. Surfaces
  // the persona-aware /dashboard plus the daily oversight workflow.
  {
    persona: "SA",
    label: "SA",
    hrefs: ["/dashboard", "/coaches", "/contracts", "/approvals", "/renewals", "/timeline", "/reports"],
  },
];

// Explicit persona-priority order. The earlier the href appears in the list,
// the higher up the sidebar it floats. Items not listed for a persona keep
// their original NAV_ITEMS order behind the prioritised ones. We preserve
// group containers but reorder both the items inside each group AND the
// groups themselves (a group's effective priority = its highest-priority
// item) so that the persona's most-relevant work surfaces are always at the
// very top of the sidebar.
const PERSONA_NAV_ORDER: Record<Persona, string[]> = {
  // Mirrors PERSONA_GROUP_DEFS so the persona group's items appear in
  // workflow order and the group itself floats to the top.
  Legal: [
    "/templates",
    "/clause-library",
    "/term-sheets",
    "/contracts",
    "/negotiations",
    "/approvals",
    "/mitigations",
    "/equity",
    "/unlock-requests",
    "/audit-log",
  ],
  Finance: [
    "/finance",
    "/analytics",
    "/contracts",
    "/renewals",
    "/equity",
    "/reports",
  ],
  HR: [
    "/coaches",
    "/term-sheets",
    "/contracts",
    "/renewals",
    "/insurance",
    "/reports",
  ],
  // SA = Sport Administrator — supervises coaches by sport.
  SA: [
    "/dashboard",
    "/coaches",
    "/contracts",
    "/approvals",
    "/renewals",
    "/timeline",
    "/reports",
  ],
  // AD = Athletic Director — top-exec role, currently inert. Defaults to
  // the standard nav order until top-exec workflows are designed.
  AD: [
    "/dashboard",
    "/contracts",
    "/approvals",
  ],
};

function getPersonaDefaultRoute(personas: Persona[], isAdmin: boolean): string {
  if (personas.length === 1) {
    if (personas[0] === "Finance") return "/contracts";
    if (personas[0] === "HR") return isAdmin ? "/templates" : "/coaches";
    if (personas[0] === "Legal") return isAdmin ? "/clause-library" : "/mitigations";
    // SA and AD both land on the persona-aware /dashboard.
  }
  return "/dashboard";
}

const PERSONA_COLORS: Record<Persona, string> = {
  Finance: "text-emerald-400 bg-emerald-500/10 border-emerald-500/30 hover:bg-emerald-500/20",
  HR: "text-blue-400 bg-blue-500/10 border-blue-500/30 hover:bg-blue-500/20",
  Legal: "text-purple-400 bg-purple-500/10 border-purple-500/30 hover:bg-purple-500/20",
  SA: "text-amber-400 bg-amber-500/10 border-amber-500/30 hover:bg-amber-500/20",
  AD: "text-slate-300 bg-slate-500/10 border-slate-500/30 hover:bg-slate-500/20",
};
const PERSONA_COLORS_ACTIVE: Record<Persona, string> = {
  Finance: "text-emerald-300 bg-emerald-500/25 border-emerald-400/60 ring-1 ring-emerald-400/40",
  HR: "text-blue-300 bg-blue-500/25 border-blue-400/60 ring-1 ring-blue-400/40",
  Legal: "text-purple-300 bg-purple-500/25 border-purple-400/60 ring-1 ring-purple-400/40",
  SA: "text-amber-300 bg-amber-500/25 border-amber-400/60 ring-1 ring-amber-400/40",
  AD: "text-slate-200 bg-slate-500/25 border-slate-400/60 ring-1 ring-slate-400/40",
};

const COLLAPSED_GROUPS_KEY = "zindeal.sidebarCollapsedGroups";

function readCollapsedGroups(): Set<string> {
  try {
    const raw = localStorage.getItem(COLLAPSED_GROUPS_KEY);
    if (!raw) return new Set();
    const parsed = JSON.parse(raw) as unknown;
    if (Array.isArray(parsed)) return new Set(parsed.filter((v): v is string => typeof v === "string"));
  } catch {
    /* ignore */
  }
  return new Set();
}

function writeCollapsedGroups(groups: Set<string>) {
  try {
    localStorage.setItem(COLLAPSED_GROUPS_KEY, JSON.stringify(Array.from(groups)));
  } catch {
    /* ignore */
  }
}

function AppSidebar() {
  const [location] = useLocation();
  const { signOut } = useClerk();
  const { user } = useUser();
  const { org } = useOrg();
  const isAdmin = org?.role === "admin";
  // Demo orgs hide the sidebar plan badge — plan tier must not surface during
  // sales demos (billing is handled by the Zindeal account team).
  const planBadge = org?.plan && !org?.isDemo ? getPlanBadgeStyle(org.plan, "sidebar") : null;
  const { theme, toggleTheme } = useTheme();
  const isDark = theme === "dark";
  const personas = (org?.personas ?? []) as Persona[];
  const { active: activePersona, setActive: setActivePersona } = useActivePersona(personas, org?.lastActivePersona ?? null);
  const effectivePersona = activePersona ?? (personas.length === 1 ? personas[0] : null);

  // Task #538: in the demo workspace, the persona switcher is wired up to
  // *also* swap the "active user" header label so the demo prospect sees
  // the same persona voices that show up in the audit log, comments, and
  // approval steps. Outside demo mode this is a no-op and the user's real
  // Clerk profile is used as before.
  const demoPersonaIdentity = (() => {
    if (!org?.isDemo || !effectivePersona) return null;
    const map: Partial<Record<Persona, { firstName: string; lastName: string; email: string }>> = {
      AD: { firstName: "Alex", lastName: "Thompson", email: "demo@zindeal.com" },
      Legal: { firstName: "Morgan", lastName: "Liu", email: "legal@demo.zindeal.com" },
      HR: { firstName: "Jamie", lastName: "Park", email: "hr@demo.zindeal.com" },
      Finance: { firstName: "Riley", lastName: "Chen", email: "finance@demo.zindeal.com" },
    };
    return map[effectivePersona] ?? null;
  })();
  const headerFirstName = demoPersonaIdentity?.firstName ?? user?.firstName ?? "";
  const headerLastName = demoPersonaIdentity?.lastName ?? user?.lastName ?? "";
  const headerEmail = demoPersonaIdentity?.email ?? user?.primaryEmailAddress?.emailAddress ?? "";
  const isLegal = personas.includes("Legal") || isAdmin;

  const unlockCountKey = getGetClauseUnlockRequestPendingCountQueryKey();
  const { data: unlockCountData } = useGetClauseUnlockRequestPendingCount({
    query: {
      queryKey: unlockCountKey,
      enabled: isLegal,
      // Match the notification bell (30s) so a freshly-filed unlock request
      // surfaces in the sidebar within the same window as the in-app
      // notification, instead of waiting up to a minute on the old poll.
      refetchInterval: 30_000,
      refetchOnWindowFocus: true,
    },
  });
  const unlockPendingCount = unlockCountData?.count ?? 0;

  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(() => readCollapsedGroups());
  const toggleGroup = (label: string) => {
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(label)) next.delete(label);
      else next.add(label);
      writeCollapsedGroups(next);
      return next;
    });
  };

  return (
    <Sidebar className="border-r">
      <SidebarHeader>
        <div className="px-3 py-4 flex items-center gap-2.5">
          <img src="/logo-dark.svg" alt="Zindeal" className="w-8 h-8 rounded-full flex-shrink-0 shadow-sm" />
          <div className="min-w-0">
            <div className="font-serif font-semibold text-base tracking-tight text-sidebar-foreground">Zindeal</div>
            <div className="text-[10px] text-sidebar-foreground/50 font-medium uppercase tracking-widest">Command Center</div>
          </div>
          {planBadge && (
            <span className={`ml-auto text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full flex-shrink-0 ${planBadge.className}`}>
              {planBadge.label}
            </span>
          )}
        </div>
        {personas.length >= 2 && (
          <div className="px-3 pb-3 space-y-1">
            <p className="text-[9px] font-semibold uppercase tracking-wider text-sidebar-foreground/40 pl-0.5">Active Persona</p>
            <div className="flex flex-wrap gap-1">
              {personas.map((p) => {
                const isActivePer = effectivePersona === p;
                return (
                  <button
                    key={p}
                    onClick={() => setActivePersona(isActivePer ? null : p)}
                    className={`text-[10px] font-semibold px-2 py-0.5 rounded border transition-all ${
                      isActivePer ? PERSONA_COLORS_ACTIVE[p] : PERSONA_COLORS[p]
                    }`}
                    title={isActivePer ? `Deactivate ${p} persona` : `Switch to ${p} persona`}
                  >
                    {p}
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </SidebarHeader>
      <SidebarContent>
        {/* Persona-first GROUP reorder driven by the explicit
            PERSONA_NAV_ORDER map. We compute a numeric priority for each
            href (lower = nearer the top), score each group by the
            best-priority item it contains, and stable-sort both groups and
            items so the persona's required surfaces land at the very top.
            Items / groups absent from the persona's map fall back to their
            original NAV_ITEMS index. */}
        {computeSidebarGroups<NavItem>({
          navItems: NAV_ITEMS,
          personaGroupDefs: PERSONA_GROUP_DEFS,
          personaNavOrder: PERSONA_NAV_ORDER,
          effectivePersona,
          isAdmin,
          isDemoMode: Boolean(org?.isDemo),
        }).map((group) => {
          const isCollapsed = collapsedGroups.has(group.label);
          const visibleItems = group.items.filter((item) => {
            if (item.financeOnly) return isAdmin || personas.includes("Finance");
            if (item.legalOnly) return isLegal;
            return true;
          });
          // Items inside the group reorder by the same explicit
          // PERSONA_NAV_ORDER priorities used for groups, so the surfaces the
          // persona cares about most appear first inside their container.
          // Items absent from the persona map keep their original NAV_ITEMS
          // order behind the prioritised ones.
          const orderedItems = effectivePersona
            ? (() => {
                const order = PERSONA_NAV_ORDER[effectivePersona];
                const prio = (href: string) => {
                  const idx = order.indexOf(href);
                  return idx === -1 ? Number.MAX_SAFE_INTEGER : idx;
                };
                return [...visibleItems]
                  .map((it, i) => ({ it, i, p: prio(it.href) }))
                  .sort((a, b) => (a.p - b.p) || (a.i - b.i))
                  .map((x) => x.it);
              })()
            : visibleItems;
          return (
            <SidebarGroup key={group.label}>
              <SidebarGroupLabel asChild>
                <button
                  type="button"
                  onClick={() => toggleGroup(group.label)}
                  className="w-full flex items-center justify-between gap-2 hover:text-sidebar-foreground transition-colors"
                  aria-expanded={!isCollapsed}
                  aria-controls={`sidebar-group-${group.label}`}
                  data-testid={`sidebar-group-toggle-${group.label.toLowerCase()}`}
                >
                  <span>{group.label}</span>
                  <ChevronRight
                    className={`h-3 w-3 transition-transform ${isCollapsed ? "" : "rotate-90"}`}
                  />
                </button>
              </SidebarGroupLabel>
              {!isCollapsed && (
                <SidebarGroupContent id={`sidebar-group-${group.label}`}>
                  <SidebarMenu>
                    {orderedItems.map((item) => {
                      const isActive =
                        item.href === "/dashboard"
                          ? location === "/dashboard"
                          : location.startsWith(item.href);
                      const isPersonaRelevant = item.personaHighlight &&
                        effectivePersona &&
                        item.personaHighlight.includes(effectivePersona);
                      const badgeCount =
                        item.showBadgeKind === "unlockRequests" ? unlockPendingCount : 0;
                      return (
                        <SidebarMenuItem key={item.href} data-tour={item.tour}>
                          <SidebarMenuButton asChild isActive={isActive}>
                            <Link href={item.href} className={`flex items-center gap-3 relative ${isActive ? "pl-4" : ""}`}>
                              {isActive && (
                                <span
                                  className="absolute left-0 top-1/2 -translate-y-1/2 w-0.5 h-5 rounded-full"
                                  style={{ background: "linear-gradient(180deg, hsl(38,95%,58%) 0%, hsl(42,90%,45%) 100%)" }}
                                />
                              )}
                              <item.icon size={15} className={isPersonaRelevant && !isActive ? "text-gold/80" : ""} />
                              <span className={isPersonaRelevant && !isActive ? "font-medium text-sidebar-foreground/90" : ""}>{item.label}</span>
                              {badgeCount > 0 ? (
                                <span
                                  className="ml-auto inline-flex items-center justify-center min-w-[18px] h-[18px] px-1.5 text-[10px] font-bold rounded-full bg-amber-500 text-amber-950 flex-shrink-0"
                                  title={`${badgeCount} pending unlock request${badgeCount === 1 ? "" : "s"}`}
                                  data-testid="badge-unlock-requests"
                                >
                                  {badgeCount > 99 ? "99+" : badgeCount}
                                </span>
                              ) : isPersonaRelevant && !isActive ? (
                                <span className="ml-auto w-1.5 h-1.5 rounded-full bg-gold/60 flex-shrink-0" title="Relevant to your persona" />
                              ) : null}
                            </Link>
                          </SidebarMenuButton>
                        </SidebarMenuItem>
                      );
                    })}
                  </SidebarMenu>
                </SidebarGroupContent>
              )}
            </SidebarGroup>
          );
        })}
      </SidebarContent>
      <SidebarFooter>
        <div className="px-2 pt-2 pb-0">
          <SidebarMenu>
            <SidebarMenuItem>
              <SidebarMenuButton asChild isActive={location === "/settings"}>
                <Link href="/settings" className="flex items-center gap-3">
                  <SettingsIcon size={15} />
                  <span>Settings</span>
                </Link>
              </SidebarMenuButton>
            </SidebarMenuItem>
            <SidebarMenuItem>
              <SidebarMenuButton
                onClick={toggleTheme}
                aria-label={isDark ? "Switch to light mode" : "Switch to control room (dark) mode"}
                aria-pressed={isDark}
                title={isDark ? "Switch to light mode" : "Switch to control room mode"}
                data-testid="theme-toggle"
                className="flex items-center gap-3"
              >
                {isDark ? <Sun size={15} /> : <Moon size={15} />}
                <span>{isDark ? "Light Mode" : "Control Room"}</span>
                <span
                  className={`ml-auto text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full ${
                    isDark
                      ? "bg-gold/20 text-gold"
                      : "bg-sidebar-accent text-sidebar-foreground/60"
                  }`}
                >
                  {isDark ? "On" : "Off"}
                </span>
              </SidebarMenuButton>
            </SidebarMenuItem>
          </SidebarMenu>
        </div>
        <div className="px-2 py-3 border-t border-sidebar-border">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="w-full flex items-center gap-2.5 px-2 py-2 rounded-lg hover:bg-sidebar-accent/60 transition-colors text-left group">
                <div className="w-7 h-7 rounded-full bg-gold/20 border border-gold/30 flex items-center justify-center text-gold text-xs font-bold flex-shrink-0">
                  {headerFirstName?.[0]}{headerLastName?.[0]}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-semibold text-sidebar-foreground truncate">
                    {headerFirstName} {headerLastName}
                  </div>
                  <div className="text-[10px] text-sidebar-foreground/40 truncate">{headerEmail}</div>
                </div>
                <div className="flex items-center gap-1 flex-shrink-0 flex-wrap justify-end max-w-[80px]">
                  {org && (
                    <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full ${isAdmin ? "bg-gold/20 text-gold" : "bg-white/10 text-sidebar-foreground/50"}`}>
                      {isAdmin ? "Admin" : "Viewer"}
                    </span>
                  )}
                  {org?.personas && org.personas.length > 0 && org.personas.slice(0, 2).map((p) => (
                    <PersonaBadge key={p} persona={p as Persona} size="xs" />
                  ))}
                  <ChevronDown className="h-3.5 w-3.5 text-sidebar-foreground/40 group-hover:text-sidebar-foreground/70 transition-colors" />
                </div>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              <DropdownMenuItem onClick={() => signOut({ redirectUrl: basePath || "/" })} className="text-destructive cursor-pointer">
                <LogOut className="h-4 w-4 mr-2" />
                Sign Out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </SidebarFooter>
    </Sidebar>
  );
}

function AppLayout({ children }: { children: React.ReactNode }) {
  return (
    <SidebarProvider>
      <div className="flex min-h-screen w-full bg-background">
        <AppSidebar />
        <main className="flex-1 flex flex-col overflow-auto">
          <ImpersonationBanner />
          <DemoBanner />
          <DemoSessionTimer />
          <LifecycleBanner />
          <div className="sticky top-0 z-20 flex items-center gap-2 sm:gap-3 border-b bg-background/95 px-3 sm:px-6 py-2 sm:py-2.5 backdrop-blur supports-[backdrop-filter]:bg-background/80">
            <SidebarTrigger className="md:hidden h-9 w-9 flex-shrink-0" />
            <div data-tour="command-palette" className="flex-1 max-w-lg">
              <CommandPalette />
            </div>
            <div className="ml-auto flex-shrink-0 flex items-center gap-1">
              <MentionsInbox />
              <NotificationCenter />
            </div>
          </div>
          <div className="flex-1 flex flex-col p-4 sm:p-6 md:p-8 min-w-0 animate-fade-in">
            {children}
          </div>
          <AppFooter />
        </main>
      </div>
    </SidebarProvider>
  );
}

/**
 * Handles the post-checkout session linking for returning users.
 * When a returning buyer signs in and lands on the dashboard with ?session_id=...,
 * this silently calls billing/checkout/link to attach the Stripe subscription to
 * their org, then cleans the session_id from the URL.
 */
function CheckoutSessionLinker() {
  const search = useSearch();
  const [, setLocation] = useLocation();
  const { org } = useOrg();
  const { getToken } = useAuth();
  const linkedRef = useRef(false);

  useEffect(() => {
    if (!org || linkedRef.current) return;
    const params = new URLSearchParams(search);
    const sessionId = params.get("session_id");
    if (!sessionId) return;

    linkedRef.current = true;

    (async () => {
      try {
        const token = await getToken();
        await fetch("/api/billing/checkout/link", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({ sessionId }),
        });
      } catch {
        // Non-fatal — the webhook may have already updated the org
      } finally {
        // Remove session_id from URL to avoid re-linking on reload
        params.delete("session_id");
        params.delete("plan");
        const newSearch = params.toString();
        setLocation(newSearch ? `/dashboard?${newSearch}` : "/dashboard", { replace: true });
      }
    })();
  }, [org, search, getToken, setLocation]);

  return null;
}

function OrgGuard({ component: Component }: { component: React.ComponentType }) {
  const { org, loading, error } = useOrg();
  const [, setLocation] = useLocation();

  useEffect(() => {
    if (!loading && error === "not_found") {
      setLocation("/onboarding");
    }
  }, [loading, error, setLocation]);

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-4">
          <img src="/logo.svg" alt="Zindeal" className="w-10 h-10 rounded-full shadow-lg animate-pulse dark:hidden" />
          <img src="/logo-dark.svg" alt="Zindeal" className="w-10 h-10 rounded-full shadow-lg animate-pulse hidden dark:block" />
          <div className="space-y-1 text-center">
            <p className="text-sm font-serif font-semibold text-foreground">Zindeal</p>
            <p className="text-xs text-muted-foreground">Loading your command center…</p>
          </div>
        </div>
      </div>
    );
  }

  if (!org) return null;

  return (
    <AppLayout>
      <CheckoutSessionLinker />
      <TwoFactorGuard>
        <RouteErrorBoundary>
          <Component />
        </RouteErrorBoundary>
      </TwoFactorGuard>
    </AppLayout>
  );
}

/**
 * Wraps the active route's page component so a render-time throw is caught
 * locally instead of unmounting the whole app shell. Keyed by `location` so
 * navigating to a different route automatically clears a previous error
 * (otherwise the user would see the fallback even after moving on).
 */
function RouteErrorBoundary({ children }: { children: React.ReactNode }) {
  const [location] = useLocation();
  return (
    <AppErrorBoundary key={location} scope={`route:${location}`}>
      {children}
    </AppErrorBoundary>
  );
}

function ProtectedRoute({ component: Component }: { component: React.ComponentType }) {
  return (
    <>
      <Show when="signed-in">
        <CoachRedirectGuard>
          <OrgGuard component={Component} />
        </CoachRedirectGuard>
      </Show>
      <Show when="signed-out">
        <Redirect to="/" />
      </Show>
    </>
  );
}

/**
 * Coach-role users are not allowed inside the staff app shell. If the
 * resolved org membership has role==="coach", bounce them to /portal —
 * which renders its own guard for users without an active coach link.
 */
function CoachRedirectGuard({ children }: { children: React.ReactNode }) {
  const { org, loading } = useOrg();
  if (loading) return null;
  if (org?.role === "coach") return <Redirect to="/portal" />;
  return <>{children}</>;
}

function HomeRedirect() {
  const { org, loading } = useOrg();
  const isAdmin = org?.role === "admin";

  const target = (() => {
    // Coaches always land in their portal — they have no internal nav.
    if (org?.role === "coach") return "/portal";
    if (!org?.personas || org.personas.length === 0) return "/dashboard";
    const personas = org.personas as Persona[];
    if (personas.length === 1) return getPersonaDefaultRoute(personas, isAdmin);
    const stored = localStorage.getItem(ACTIVE_PERSONA_STORAGE_KEY) as Persona | null;
    if (stored && (personas as string[]).includes(stored)) {
      return getPersonaDefaultRoute([stored], isAdmin);
    }
    const remembered = org?.lastActivePersona ?? null;
    if (remembered && (personas as string[]).includes(remembered)) {
      return getPersonaDefaultRoute([remembered], isAdmin);
    }
    return "/dashboard";
  })();

  return (
    <>
      <Show when="signed-in">
        {loading ? null : org ? <Redirect to={target} /> : <Redirect to="/onboarding" />}
      </Show>
      <Show when="signed-out">
        <Landing />
      </Show>
    </>
  );
}

function AuthPageShell({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen flex bg-background">
      {/* Left brand panel — hidden on small screens */}
      <div className="hidden lg:flex lg:w-96 xl:w-[440px] flex-col justify-between p-10 bg-navy text-white flex-shrink-0 relative overflow-hidden">
        {/* Background decoration */}
        <div className="absolute inset-0 opacity-10 pointer-events-none select-none">
          <div className="absolute top-0 right-0 w-64 h-64 rounded-full bg-gold blur-3xl" />
          <div className="absolute bottom-0 left-0 w-48 h-48 rounded-full bg-blue-400 blur-3xl" />
        </div>
        <div className="relative">
          <div className="flex items-center gap-2.5 mb-8">
            <img src="/logo-dark.svg" alt="Zindeal" className="w-9 h-9 shadow-md rounded-full" />
            <span className="font-serif font-semibold text-xl tracking-tight">Zindeal</span>
          </div>
          <p className="text-3xl font-bold leading-snug tracking-tight text-white">
            The front office for coach contracts.
          </p>
          <p className="mt-4 text-white/50 text-sm leading-relaxed">
            Executive-grade contract management trusted by ADs, compliance officers, and CFOs across the country.
          </p>
        </div>
        <div className="relative space-y-4">
          {[
            { stat: "46", label: "NCAA Sports supported" },
            { stat: "100%", label: "Compliance visibility" },
            { stat: "90-day", label: "Proactive renewal alerts" },
          ].map((item) => (
            <div key={item.label} className="flex items-center gap-3">
              <div className="text-2xl font-extrabold text-gold kpi-value">{item.stat}</div>
              <div className="text-white/50 text-sm">{item.label}</div>
            </div>
          ))}
        </div>
      </div>
      {/* Right auth form */}
      <div className="flex-1 flex flex-col items-center justify-center px-4 py-12 bg-background">
        <div className="lg:hidden flex items-center gap-2 mb-8">
          <img src="/logo.svg" alt="Zindeal" className="w-8 h-8 rounded-full dark:hidden" />
          <img src="/logo-dark.svg" alt="Zindeal" className="w-8 h-8 rounded-full hidden dark:block" />
          <span className="font-serif font-semibold text-lg tracking-tight">Zindeal</span>
        </div>
        {children}
      </div>
    </div>
  );
}

function SignInPage() {
  const search = typeof window !== "undefined" ? window.location.search : "";
  const params = new URLSearchParams(search);
  const sessionId = params.get("session_id");
  const plan = params.get("plan");

  let redirectUrl = `${basePath}/dashboard`;
  if (sessionId) {
    const qs = new URLSearchParams({ session_id: sessionId });
    if (plan) qs.set("plan", plan);
    redirectUrl = `${basePath}/dashboard?${qs.toString()}`;
  }

  return (
    <AuthPageShell>
      <div className="flex flex-col items-center gap-0 w-full">
        <SignIn
          routing="path"
          path={`${basePath}/sign-in`}
          signUpUrl={`${basePath}/sign-up`}
          forceRedirectUrl={redirectUrl}
        />
      </div>
    </AuthPageShell>
  );
}

function SignUpPage() {
  return (
    <AuthPageShell>
      <div className="flex flex-col items-center gap-4 w-full">
        <SignUp
          routing="path"
          path={`${basePath}/sign-up`}
          signInUrl={`${basePath}/sign-in`}
          forceRedirectUrl={`${basePath}/onboarding`}
        />
        <p className="text-xs text-muted-foreground text-center max-w-sm">
          By signing up, you agree to our{" "}
          <Link href="/terms" className="underline hover:text-foreground transition-colors">
            Terms of Service
          </Link>{" "}
          and{" "}
          <Link href="/privacy" className="underline hover:text-foreground transition-colors">
            Privacy Policy
          </Link>
          .
        </p>
      </div>
    </AuthPageShell>
  );
}

function ClerkQueryClientCacheInvalidator() {
  const { addListener } = useClerk();
  const qc = useQueryClient();
  const prevUserIdRef = useRef<string | null | undefined>(undefined);

  useEffect(() => {
    const unsubscribe = addListener(({ user }) => {
      const userId = user?.id ?? null;
      if (prevUserIdRef.current !== undefined && prevUserIdRef.current !== userId) {
        qc.clear();
      }
      prevUserIdRef.current = userId;
    });
    return unsubscribe;
  }, [addListener, qc]);

  return null;
}

/**
 * Demo entry for sales-presenter and owner-issued invite links only.
 * Public self-serve email-gate is disabled (see DEMO_SELF_SERVE_ENABLED on
 * the API). Bare `/demo` routes to a sales contact CTA.
 */
function DemoPage() {
  const { isSignedIn, isLoaded: userLoaded } = useUser();
  const { signIn, fetchStatus } = useSignIn();
  const [, setLocation] = useLocation();

  const expiredFromTimeout =
    typeof window !== "undefined" &&
    new URLSearchParams(window.location.search).get("expired") === "1";
  // Task #448: owner-issued invitation token.
  const inviteToken =
    typeof window !== "undefined"
      ? new URLSearchParams(window.location.search).get("invite")
      : null;
  // Sales-presenter mode: Accept `?presenter=1` (cookie on this browser) and
  // `?sales=1` (redeem endpoint redirect).
  const presenterFlag = (() => {
    if (typeof window === "undefined") return false;
    const q = new URLSearchParams(window.location.search);
    return q.get("presenter") === "1" || q.get("sales") === "1";
  })();
  const [presenterError, setPresenterError] = useState<string | null>(null);
  const [presenterTicket, setPresenterTicket] = useState<string | null>(null);
  const [presenterSigningIn, setPresenterSigningIn] = useState(false);

  useEffect(() => {
    if (!presenterFlag) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch("/api/demo/presenter/start", {
          method: "POST",
          credentials: "include",
        });
        const data = (await r.json().catch(() => ({}))) as {
          ok?: boolean;
          signInToken?: string;
          error?: string;
        };
        if (cancelled) return;
        if (!r.ok || !data.ok || !data.signInToken) {
          setPresenterError(data.error ?? "presenter_unavailable");
          return;
        }
        setPresenterTicket(data.signInToken);
      } catch {
        if (!cancelled) setPresenterError("network_error");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [presenterFlag]);

  useEffect(() => {
    if (!presenterTicket || !signIn || fetchStatus !== "idle" || presenterSigningIn) return;
    let cancelled = false;
    setPresenterSigningIn(true);
    (async () => {
      try {
        const createResult = await signIn.create({
          strategy: "ticket",
          ticket: presenterTicket,
        });
        if (cancelled) return;
        if (createResult.error || signIn.status !== "complete") {
          setPresenterError("sign_in_failed");
          setPresenterTicket(null);
          return;
        }
        await signIn.finalize({
          navigate: () => setLocation("/dashboard", { replace: true }),
        });
      } catch {
        if (!cancelled) {
          setPresenterError("sign_in_failed");
          setPresenterTicket(null);
        }
      } finally {
        if (!cancelled) setPresenterSigningIn(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [presenterTicket, signIn, fetchStatus, presenterSigningIn, setLocation]);

  const [inviteRedeemed, setInviteRedeemed] = useState<{
    tier: string;
    persona: string | null;
    label: string;
  } | null>(null);
  const [inviteError, setInviteError] = useState<string | null>(null);
  const [inviteTicket, setInviteTicket] = useState<string | null>(null);
  const [inviteSigningIn, setInviteSigningIn] = useState(false);

  useEffect(() => {
    if (!inviteToken) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch("/api/demo/invite/redeem", {
          method: "POST",
          headers: { "content-type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ token: inviteToken }),
        });
        const data = (await r.json().catch(() => ({}))) as {
          ok?: boolean;
          tier?: string;
          persona?: string | null;
          label?: string;
          signInToken?: string | null;
          error?: string;
        };
        if (cancelled) return;
        if (!r.ok || !data.ok) {
          setInviteError(data.error ?? "invitation_invalid");
          return;
        }
        setInviteRedeemed({
          tier: data.tier ?? "standard",
          persona: data.persona ?? null,
          label: data.label ?? "",
        });
        if (data.signInToken) {
          setInviteTicket(data.signInToken);
        } else {
          setInviteError("sign_in_unavailable");
        }
      } catch {
        if (!cancelled) setInviteError("network_error");
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inviteToken]);

  useEffect(() => {
    if (!inviteTicket || !signIn || fetchStatus !== "idle" || inviteSigningIn) return;
    let cancelled = false;
    setInviteSigningIn(true);
    (async () => {
      try {
        const createResult = await signIn.create({
          strategy: "ticket",
          ticket: inviteTicket,
        });
        if (cancelled) return;
        if (createResult.error || signIn.status !== "complete") {
          setInviteError("sign_in_failed");
          setInviteTicket(null);
          return;
        }
        await signIn.finalize({
          navigate: () => setLocation("/dashboard", { replace: true }),
        });
      } catch {
        if (!cancelled) {
          setInviteError("sign_in_failed");
          setInviteTicket(null);
        }
      } finally {
        if (!cancelled) setInviteSigningIn(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [inviteTicket, signIn, fetchStatus, inviteSigningIn, setLocation]);

  useEffect(() => {
    if (userLoaded && isSignedIn) {
      setLocation("/dashboard", { replace: true });
    }
  }, [userLoaded, isSignedIn, setLocation]);

  const showPresenterPending = presenterFlag && !presenterError;
  const showInvitePending = Boolean(inviteToken && !inviteError);
  const showContactSales = !showPresenterPending && !showInvitePending;

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-indigo-50 via-white to-amber-50 px-4 py-10">
      <div className="w-full max-w-lg bg-white rounded-2xl shadow-xl border border-indigo-100 p-8 sm:p-10">
        <div className="flex items-center gap-3 mb-6">
          <div className="h-11 w-11 rounded-xl bg-indigo-600 flex items-center justify-center">
            <Sparkles className="h-5 w-5 text-white" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-900">
              {showPresenterPending || showInvitePending
                ? "Zindeal Live Sandbox"
                : "Schedule a walkthrough"}
            </h1>
            <p className="text-xs text-gray-500">
              {showPresenterPending || showInvitePending
                ? "Signing you into a guided demo session…"
                : "Live product demos are available through our team."}
            </p>
          </div>
        </div>

        {expiredFromTimeout && showContactSales && (
          <div
            className="mb-4 rounded-xl border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900"
            data-testid="demo-expired-banner"
          >
            Your previous demo session ended. Contact sales to schedule another
            walkthrough.
          </div>
        )}

        {inviteRedeemed && (
          <div
            data-testid="demo-invite-banner"
            className={`mb-4 rounded-xl border p-3 text-sm ${
              inviteRedeemed.tier === "enterprise"
                ? "border-violet-300 bg-violet-50 text-violet-900"
                : "border-indigo-200 bg-indigo-50 text-indigo-900"
            }`}
          >
            <div className="font-semibold">
              {inviteRedeemed.tier === "enterprise"
                ? "Enterprise Preview invitation accepted"
                : "Demo invitation accepted"}
              {inviteRedeemed.label ? ` · ${inviteRedeemed.label}` : ""}
            </div>
            {inviteRedeemed.tier === "enterprise" && (
              <p className="mt-1 text-xs leading-snug">
                The full product surface will be unlocked for this 15-minute
                session — including bulk imports, AI features and unwatermarked
                exports.
              </p>
            )}
            {inviteRedeemed.persona && (
              <p className="mt-1 text-xs">
                Workspace will boot into the {inviteRedeemed.persona} persona.
              </p>
            )}
          </div>
        )}

        {inviteError && (
          <div
            data-testid="demo-invite-error"
            className="mb-4 rounded-xl border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900"
          >
            That invitation link is no longer valid (
            {inviteError.replace(/_/g, " ")}). Contact sales for a guided
            walkthrough.
          </div>
        )}

        {showPresenterPending ? (
          <div
            data-testid="demo-presenter-pending"
            className="rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-900 text-center"
          >
            <Loader2 className="h-5 w-5 animate-spin mx-auto mb-2" />
            Presenter mode — signing you in to the sandbox…
          </div>
        ) : presenterError ? (
          <div
            data-testid="demo-presenter-error"
            className="mb-4 rounded-xl border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900"
          >
            Presenter session unavailable (
            {presenterError.replace(/_/g, " ")}). Ask an owner-admin to issue a
            fresh sales-presenter link, or contact sales below.
          </div>
        ) : null}

        {showInvitePending ? (
          <div
            data-testid="demo-invite-pending"
            className="rounded-xl border border-indigo-200 bg-indigo-50 p-4 text-sm text-indigo-900 text-center"
          >
            <Loader2 className="h-5 w-5 animate-spin mx-auto mb-2" />
            {inviteRedeemed
              ? "Signing you in to the sandbox…"
              : "Validating your invitation…"}
          </div>
        ) : null}

        {showContactSales && (
          <div className="space-y-4" data-testid="demo-contact-sales">
            <p className="text-gray-700 leading-relaxed">
              We&apos;ll set up a guided session tailored to your athletic
              department — no self-serve sandbox on this site.
            </p>
            <a
              href={salesInquiryHref("Demo walkthrough request from /demo")}
              className="w-full flex items-center justify-center gap-2 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white font-semibold py-3 px-4 transition-colors"
              data-testid="button-demo-contact-sales"
            >
              Talk to sales
            </a>
          </div>
        )}

        <div className="mt-6 pt-6 border-t border-gray-100 flex items-center justify-between text-sm">
          <Link
            href="/sign-up"
            className="text-indigo-600 font-medium hover:underline"
          >
            Sign up instead
          </Link>
          <Link href="/welcome" className="text-gray-500 hover:text-gray-700">
            Back to home
          </Link>
        </div>
      </div>
    </div>
  );
}

function AppRouter() {
  const [, setLocation] = useLocation();

  return (
    <ClerkProvider
      publishableKey={clerkPubKey!}
      proxyUrl={clerkProxyUrl || undefined}
      // @ts-expect-error clerkJSUrl is a valid runtime prop in Clerk v6 but not reflected in the TS types
      clerkJSUrl={clerkJsUrl}
      appearance={clerkAppearance}
      signInUrl={`${basePath}/sign-in`}
      signUpUrl={`${basePath}/sign-up`}
      localization={{
        signIn: {
          start: { title: "Welcome back", subtitle: "Sign in to your Zindeal account" },
        },
        signUp: {
          start: { title: "Create your account", subtitle: "Start managing coaching contracts today" },
        },
      }}
      routerPush={(to) => setLocation(stripBase(to))}
      routerReplace={(to) => setLocation(stripBase(to), { replace: true })}
    >
      <QueryClientProvider client={queryClient}>
        <ClerkQueryClientCacheInvalidator />
        <ReadOnlyToastHandler />
        <VersionDriftHandler />
        <OrgProvider>
        <OnboardingProvider>
        <TooltipProvider>
          <ProductTour />
          <Switch>
            <Route path="/admin/login" component={AdminLogin} />
            <Route path="/admin/health" component={() => <AdminGuard component={AdminHealth} />} />
            <Route path="/admin/orgs/:id/contracts/:contractId" component={() => <AdminGuard component={AdminContractDetail} />} />
            <Route path="/admin/orgs/:id/coaches/:coachId" component={() => <AdminGuard component={AdminCoachDetail} />} />
            <Route path="/admin/orgs/:id" component={() => <AdminGuard component={AdminOrgDetail} />} />
            <Route path="/admin/demo-leads" component={() => <AdminGuard component={AdminDemoLeads} />} />
            <Route path="/admin" component={() => <AdminGuard component={AdminDashboard} />} />
            {/* Task #383: Guest Legal Reviewer routes — registered BEFORE the
                root route. They render their own GuestLayout and skip the
                main app's sidebar/OrgGuard so guests never see internal nav. */}
            <Route path="/guest/accept" component={GuestAccept} />
            <Route path="/coach-portal/accept" component={CoachPortalAccept} />
            <Route path="/guest/contracts/:id" component={GuestContractPage} />
            <Route path="/" component={HomeRedirect} />
            <Route path="/welcome" component={Landing} />
            <Route path="/demo" component={DemoPage} />
            <Route path="/sign-in/*?" component={SignInPage} />
            <Route path="/sign-up/*?" component={SignUpPage} />
            <Route path="/onboarding" component={Onboarding} />
            <Route path="/portal" component={CoachPortal} />
            <Route path="/dashboard" component={() => <ProtectedRoute component={Dashboard} />} />
            <Route path="/contracts/import" component={() => <ProtectedRoute component={ContractsImport} />} />
            <Route path="/contracts/new" component={() => <ProtectedRoute component={ContractNew} />} />
            <Route path="/contracts/:id" component={() => <ProtectedRoute component={ContractDetail} />} />
            <Route path="/contracts" component={() => <ProtectedRoute component={Contracts} />} />
            <Route path="/term-sheets/new" component={() => <ProtectedRoute component={TermSheetNew} />} />
            <Route path="/term-sheets/:id" component={() => <ProtectedRoute component={TermSheetDetail} />} />
            <Route path="/term-sheets" component={() => <ProtectedRoute component={Contracts} />} />
            <Route path="/timeline" component={() => <ProtectedRoute component={Timeline} />} />
            <Route path="/coaches/new" component={() => <ProtectedRoute component={CoachNew} />} />
            <Route path="/coaches/:id" component={() => <ProtectedRoute component={CoachDetail} />} />
            <Route path="/coaches" component={() => <ProtectedRoute component={Coaches} />} />
            <Route path="/counterparties" component={() => <ProtectedRoute component={Counterparties} />} />
            <Route path="/insurance" component={() => <ProtectedRoute component={Insurance} />} />
            <Route path="/mitigations" component={() => <ProtectedRoute component={Mitigations} />} />
            <Route path="/renewals" component={() => <ProtectedRoute component={Renewals} />} />
            <Route path="/settings" component={() => <ProtectedRoute component={Settings} />} />
            <Route path="/notifications" component={() => <ProtectedRoute component={Notifications} />} />
            <Route path="/approvals" component={() => <ProtectedRoute component={Approvals} />} />
            <Route path="/negotiations" component={() => <ProtectedRoute component={Negotiations} />} />
            <Route path="/audit-log" component={() => <ProtectedRoute component={AuditLog} />} />
            <Route path="/clause-library/new" component={() => <ProtectedRoute component={() => <ClauseEditor mode="new" />} />} />
            <Route path="/clause-library/:id/edit" component={() => <ProtectedRoute component={() => <ClauseEditor mode="edit" />} />} />
            <Route path="/clause-library/:id" component={() => <ProtectedRoute component={ClauseDetail} />} />
            <Route path="/clause-library" component={() => <ProtectedRoute component={ClauseLibrary} />} />
            <Route path="/unlock-requests" component={() => <ProtectedRoute component={UnlockRequests} />} />
            <Route path="/analytics" component={() => <ProtectedRoute component={Analytics} />} />
            <Route path="/equity" component={() => <ProtectedRoute component={Equity} />} />
            <Route path="/reports" component={() => <ProtectedRoute component={Reports} />} />
            <Route path="/finance" component={() => <ProtectedRoute component={Finance} />} />
            <Route path="/compensation-verification" component={() => <ProtectedRoute component={CompensationVerification} />} />
            <Route path="/templates/new" component={() => <ProtectedRoute component={() => <TemplatesEditor mode="new" />} />} />
            <Route path="/templates/:id/edit" component={() => <ProtectedRoute component={() => <TemplatesEditor mode="edit" />} />} />
            <Route path="/templates" component={() => <ProtectedRoute component={Templates} />} />
            <Route path="/sign/:approvalId" component={() => <ProtectedRoute component={SignPage} />} />
            <Route path="/approval-action/:token" component={ApprovalActionPage} />
            <Route path="/terms" component={Terms} />
            <Route path="/privacy" component={Privacy} />
            <Route path="/security" component={SecurityPage} />
            <Route path="/subprocessors" component={Subprocessors} />
            <Route component={NotFound} />
          </Switch>
        </TooltipProvider>
        <CookieConsent />
        <HubSpotTracker />
        <Toaster />
        </OnboardingProvider>
        </OrgProvider>
      </QueryClientProvider>
    </ClerkProvider>
  );
}

function ErrorFallback({ error, resetError }: { error: unknown; resetError: () => void }) {
  return (
    <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", padding: "2rem", fontFamily: "system-ui, sans-serif" }}>
      <div style={{ maxWidth: 480, textAlign: "center" }}>
        <h1 style={{ fontSize: "1.5rem", fontWeight: 600, marginBottom: "0.75rem" }}>Something went wrong</h1>
        <p style={{ color: "#666", marginBottom: "1.5rem" }}>
          The page hit an unexpected error. Our team has been notified.
        </p>
        {import.meta.env.DEV && (
          <pre style={{ textAlign: "left", background: "#f6f6f6", padding: "0.75rem", borderRadius: 6, overflow: "auto", fontSize: 12, marginBottom: "1.5rem" }}>
            {(error as Error)?.message ?? String(error)}
          </pre>
        )}
        <button
          onClick={() => { resetError(); window.location.reload(); }}
          style={{ padding: "0.5rem 1rem", background: "#111", color: "#fff", border: 0, borderRadius: 6, cursor: "pointer" }}
        >
          Reload page
        </button>
      </div>
    </div>
  );
}

initAnalytics();

function App() {
  return (
    <Sentry.ErrorBoundary fallback={({ error, resetError }) => <ErrorFallback error={error} resetError={resetError} />}>
      <WouterRouter base={basePath}>
        <AppRouter />
      </WouterRouter>
    </Sentry.ErrorBoundary>
  );
}

export default App;
